import React, { useState, useEffect, useCallback, useRef } from "react";
import { getAddressList } from "../services/addressService";
import Pagination from "../components/Pagination";
import { SearchIcon, EditIcon } from "../assets/icons";
import UpdateAddressModal from "../components/AddressModals/updateAddressModal";
import { Toast } from "primereact/toast";

export default function AddressList() {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    
    // Search states
    const [kapinoSearch, setKapinoSearch] = useState("");
    const [fulladresSearch, setFulladresSearch] = useState("");

    // Store active search filters separately from input values
    const [activeKapino, setActiveKapino] = useState("");
    const [activeFulladres, setActiveFulladres] = useState("");

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);

    // Toast ref
    const toast = useRef(null);

    const fetchAddresses = useCallback(async () => {
        try {
            const params = {
                count: 20, 
                offset: currentPage,
            };

            // Add search parameters if they exist (use active filters, not input values)
            if (activeKapino.trim()) params.kapino = activeKapino.trim();
            if (activeFulladres.trim()) params.fulladres = activeFulladres.trim();
            
            const response = await getAddressList(params);
            
            setAddresses(response.data);
            setTotalPages(Math.ceil(response.totalRecords / 20));
        } catch (error) {
            setError(error.message || 'Veri yükleme hatası oluştu');
        } finally {
            setLoading(false);
        }
    }, [currentPage, activeKapino, activeFulladres]);

    // Handle search
    const handleSearch = () => {
        // Update active filters with current input values
        setActiveKapino(kapinoSearch);
        setActiveFulladres(fulladresSearch);
        // Reset to page 0 when searching (offset = 0)
        setCurrentPage(0);
    };

    // Handle search on Enter key
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleEditClick = (address) => {
        setSelectedAddress(address);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedAddress(null);
    };

    const handleUpdateSuccess = () => {
        // Show success toast
        toast.current.show({
            severity: 'success',
            summary: 'Başarılı',
            detail: 'Adres başarıyla güncellendi!',
            life: 3000
        });

        // Refresh the list with current filters
        fetchAddresses();
    };

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchAddresses();
    }, [currentPage, fetchAddresses]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="address-list-container">
            <Toast ref={toast} />
            
            <div className="search-container">
                <div className="search-inputs">
                    {/* Kapı No search input */}
                    <div className="search-input-group">
                        <input
                            type="text"
                            placeholder="Kapı No ara..."
                            value={kapinoSearch}
                            onChange={(e) => setKapinoSearch(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            className="search-input"
                        />
                    </div>

                    {/* Full Address search input */}
                    <div className="search-input-group">
                        <input
                            type="text"
                            placeholder="Adres ara..."
                            value={fulladresSearch}
                            onChange={(e) => setFulladresSearch(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            className="search-input full-address-input"
                        />
                        <button 
                            onClick={handleSearch}
                            className="search-button"
                        >
                            <SearchIcon className="search-icon" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="table-container">
                <table className="address-table">
                    <thead>
                        <tr>
                            <th>Tam Adres</th>
                            <th>İlçe</th>
                            <th>Mahalle</th>
                            <th>Sokak</th>
                            <th>Apartman</th>
                            <th>Kapı No</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {addresses.map((address) => (
                            <tr key={address.id}>
                                <td>{address.fullAdres}</td>
                                <td>{address.ilce}</td>
                                <td>{address.mah}</td>
                                <td>{address.sokak}</td>
                                <td>{address.apartman}</td>
                                <td>{address.kapiNo}</td>
                                <td>
                                    <button 
                                        className="icon-button edit" 
                                        onClick={() => handleEditClick(address)}
                                    >
                                        <EditIcon color='#18964E' />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            <UpdateAddressModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                address={selectedAddress}
                onSuccess={handleUpdateSuccess}
            />
        </div>
    );
}